import { resolveApiEndpoint, buildHeaders } from '../utils/apiConfig';
import { isFormDataLike, sleep, normalizeHeaders } from '../utils/http';
import { consumeStreamingResponse } from '../utils/streaming';

export interface ApiError {
  message: string;
  status?: number;
  body?: unknown;
}

export type ResponseTypeHint = 'auto' | 'json' | 'text' | 'blob' | 'arrayBuffer' | 'response';
export type StreamMode = 'disabled' | 'auto';

/**
 * Fallback 配置：当主请求失败时，使用候补端点重试
 */
export interface FallbackConfig {
  /** 候补端点列表，按优先级顺序尝试 */
  endpoints: string[];
  /**
   * 触发 fallback 的条件（默认：429 或响应包含 RESOURCE_EXHAUSTED）
   * - 'resourceExhausted': 资源不足时触发（429 或 RESOURCE_EXHAUSTED 错误）
   * - 'serverError': 服务器错误时触发（5xx）
   * - 'any': 任何错误都触发
   * - 自定义函数：(error: ApiError) => boolean
   */
  triggerOn?: 'resourceExhausted' | 'serverError' | 'any' | ((error: ApiError) => boolean);
  /** fallback 重试前的延迟毫秒数（默认 1000ms） */
  delayMs?: number;
}

export interface RequestOptions {
  method?: string;
  endpoint: string;
  headers?: Record<string, unknown>;
  body?: unknown;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  responseType?: ResponseTypeHint;
  includeAuth?: boolean;
  resolveEndpoint?: boolean;
  cache?: RequestCache;
  contentType?: string | null;
  streamMode?: StreamMode;
  onStream?: (aggregated: string) => void;
  /** Fallback 配置：主请求失败时的候补端点策略 */
  fallback?: FallbackConfig;
}

const defaultTimeout = 30000;
const defaultRetries = 0;
const defaultRetryDelay = 500;

const isApiError = (value: unknown): value is ApiError => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message?: unknown }).message === 'string'
  );
};

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  if (!ms || ms <= 0) return promise;
  let timerId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new Error('Request timeout')), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timerId) {
      clearTimeout(timerId);
    }
  }
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

function shouldAttachJsonHeader(
  body: unknown,
  headers: Record<string, string>,
  contentTypeDisabled: boolean
): boolean {
  if (contentTypeDisabled) return false;
  if (headers['Content-Type'] || headers['content-type']) return false;
  if (body === undefined || body === null) return false;
  if (typeof body === 'string' || body instanceof String) return false;
  if (isFormDataLike(body)) return false;
  return true;
}

function normalizeBody(body: unknown): BodyInit | undefined {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (isFormDataLike(body)) {
    return body;
  }
  if (typeof body === 'string' || body instanceof String) {
    return body.toString();
  }
  return JSON.stringify(body);
}

async function parseResponse<T>(res: Response, hint: ResponseTypeHint): Promise<T> {
  switch (hint) {
    case 'json':
      return (await res.json()) as T;
    case 'text':
      return (await res.text()) as unknown as T;
    case 'blob':
      return (await res.blob()) as unknown as T;
    case 'arrayBuffer':
      return (await res.arrayBuffer()) as unknown as T;
    case 'response':
      return res as unknown as T;
    case 'auto':
    default: {
      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const data = (await res.json()) as T;
        return data;
      }
      const txt = await res.text();
      return txt as unknown as T;
    }
  }
}

/**
 * 检测错误是否为资源不足类型（429 或 RESOURCE_EXHAUSTED）
 */
function isResourceExhaustedError(error: ApiError): boolean {
  // HTTP 429 Too Many Requests
  if (error.status === 429) {
    return true;
  }
  // Google API 的 RESOURCE_EXHAUSTED 错误
  const bodyStr = typeof error.body === 'string'
    ? error.body
    : JSON.stringify(error.body ?? '');
  if (bodyStr.includes('RESOURCE_EXHAUSTED') || bodyStr.includes('resource_exhausted')) {
    return true;
  }
  // 检查错误消息
  if (error.message.includes('RESOURCE_EXHAUSTED') || error.message.includes('resource exhausted')) {
    return true;
  }
  return false;
}

/**
 * 判断是否应该触发 fallback
 */
function shouldTriggerFallback(
  error: ApiError,
  triggerOn: FallbackConfig['triggerOn']
): boolean {
  if (typeof triggerOn === 'function') {
    return triggerOn(error);
  }
  switch (triggerOn) {
    case 'resourceExhausted':
      return isResourceExhaustedError(error);
    case 'serverError':
      return typeof error.status === 'number' && error.status >= 500;
    case 'any':
      return true;
    default:
      // 默认行为：资源不足时触发
      return isResourceExhaustedError(error);
  }
}

/**
 * 通用的 fetch 封装：
 * - 使用 resolveApiEndpoint() 解析终端地址
 * - 使用 buildHeaders() 构建统一的请求头
 * - 支持 JSON、FormData、字符串请求体
 * - 提供超时与简单的重试支持
 * - 支持 fallback 端点：主端点失败时自动切换到候补端点
 */
export async function request<T = unknown>(opts: RequestOptions): Promise<T> {
  const {
    method = 'GET',
    endpoint,
    headers,
    body,
    timeoutMs = defaultTimeout,
    retries = defaultRetries,
    retryDelayMs = defaultRetryDelay,
    responseType = 'auto',
    includeAuth = true,
    resolveEndpoint = true,
    cache,
    contentType,
    streamMode = 'disabled',
    onStream,
    fallback,
  } = opts;

  const determineCredentials = (targetUrl: string | undefined): RequestCredentials => {
    if (typeof window === 'undefined') {
      return 'include';
    }
    if (!targetUrl) {
      return 'include';
    }
    try {
      const target = new URL(targetUrl, window.location.origin);
      return target.origin === window.location.origin ? 'include' : 'omit';
    } catch {
      return 'omit';
    }
  };

  const findHeaderKey = (target: Record<string, string>, name: string): string | undefined => {
    const lower = name.toLowerCase();
    return Object.keys(target).find((key) => key.toLowerCase() === lower);
  };

  const removeHeader = (target: Record<string, string>, name: string): void => {
    const key = findHeaderKey(target, name);
    if (key) {
      delete target[key];
    }
  };

  const hasHeader = (target: Record<string, string>, name: string): boolean => Boolean(findHeaderKey(target, name));

  const setHeaderIfAbsent = (target: Record<string, string>, name: string, value: string): void => {
    if (!hasHeader(target, name)) {
      target[name] = value;
    }
  };

  const effectiveStreamMode: StreamMode =
    streamMode === 'disabled' && typeof onStream === 'function' ? 'auto' : streamMode;

  /**
   * 内部请求函数：执行单个端点的请求（含重试）
   */
  const executeRequest = async (targetEndpoint: string): Promise<T> => {
    const url = resolveEndpoint ? resolveApiEndpoint(targetEndpoint) : targetEndpoint;
    let attempt = 0;
    let lastErr: unknown = null;

    while (attempt <= retries) {
      try {
        const normalizedHeaderInput = normalizeHeaders(headers as Record<string, unknown> | undefined);
        const baseHeaders = includeAuth
          ? buildHeaders(normalizedHeaderInput)
          : { ...normalizedHeaderInput };

        const disableContentType = contentType === null;

        if (disableContentType) {
          removeHeader(baseHeaders, 'content-type');
        } else if (typeof contentType === 'string' && contentType.trim()) {
          setHeaderIfAbsent(baseHeaders, 'Content-Type', contentType.trim());
        }

        const requestUrl = url;

        const init: RequestInit = {
          method,
          headers: baseHeaders,
          credentials: determineCredentials(requestUrl),
        };

        if (cache) {
          init.cache = cache;
        }

        if (body !== undefined && body !== null) {
          if (isFormDataLike(body)) {
            const headersForForm = { ...baseHeaders };
            removeHeader(headersForForm, 'content-type');
            init.headers = headersForForm;
            init.body = body;
          } else {
            const shouldAttachJson = shouldAttachJsonHeader(body, baseHeaders, disableContentType);
            const headersForPayload = shouldAttachJson
              ? { ...baseHeaders, 'Content-Type': 'application/json' }
              : baseHeaders;
            init.headers = headersForPayload;
            init.body = normalizeBody(body);
          }
        }

        const resp = await withTimeout(fetch(requestUrl, init), timeoutMs);
        if (!resp.ok) {
          const parsed = await parseJsonSafe(resp);
          let fallbackBody: unknown = parsed;
          if (fallbackBody === undefined) {
            try {
              fallbackBody = await resp.text();
            } catch {
              fallbackBody = undefined;
            }
          }
          const err: ApiError = { message: `HTTP ${resp.status}`, status: resp.status, body: fallbackBody };
          if (resp.status >= 500 && attempt < retries) {
            attempt++;
            await sleep(retryDelayMs);
            continue;
          }
          throw err;
        }

        if (effectiveStreamMode !== 'disabled') {
          return (await consumeStreamingResponse(resp, { onChunk: onStream })) as T;
        }

        return await parseResponse<T>(resp, responseType);
      } catch (error) {
        lastErr = error;
        if (attempt < retries) {
          attempt++;
          await sleep(retryDelayMs);
          continue;
        }
        if (isApiError(error)) throw error;
        const message = error instanceof Error ? error.message : 'Network error';
        throw { message, body: null } as ApiError;
      }
    }
    throw lastErr;
  };

  // 执行主请求
  try {
    return await executeRequest(endpoint);
  } catch (primaryError) {
    // 检查是否配置了 fallback 且应该触发
    if (
      fallback &&
      Array.isArray(fallback.endpoints) &&
      fallback.endpoints.length > 0 &&
      isApiError(primaryError) &&
      shouldTriggerFallback(primaryError, fallback.triggerOn)
    ) {
      console.log(
        `[request] 主端点 ${endpoint} 请求失败 (${primaryError.message})，开始尝试 fallback 端点...`
      );

      const fallbackDelay = fallback.delayMs ?? 1000;

      // 依次尝试 fallback 端点
      for (let i = 0; i < fallback.endpoints.length; i++) {
        const fallbackEndpoint = fallback.endpoints[i];
        console.log(
          `[request] 尝试 fallback 端点 [${i + 1}/${fallback.endpoints.length}]: ${fallbackEndpoint}`
        );

        // 在尝试 fallback 前等待
        await sleep(fallbackDelay);

        try {
          const result = await executeRequest(fallbackEndpoint);
          console.log(`[request] Fallback 端点 ${fallbackEndpoint} 请求成功`);
          return result;
        } catch (fallbackError) {
          console.warn(
            `[request] Fallback 端点 ${fallbackEndpoint} 请求失败:`,
            isApiError(fallbackError) ? fallbackError.message : fallbackError
          );
          // 继续尝试下一个 fallback 端点
        }
      }

      // 所有 fallback 端点都失败了，抛出原始错误
      console.error('[request] 所有 fallback 端点均已耗尽，抛出原始错误');
    }

    // 没有 fallback 配置或不满足触发条件，直接抛出原始错误
    throw primaryError;
  }
}

export async function getJson<T = unknown>(endpoint: string, opts?: Partial<RequestOptions>) {
  return request<T>({ method: 'GET', endpoint, ...(opts || {}), responseType: opts?.responseType ?? 'json' });
}

export async function postJson<T = unknown>(endpoint: string, body?: unknown, opts?: Partial<RequestOptions>) {
  return request<T>({ method: 'POST', endpoint, body, ...(opts || {}), responseType: opts?.responseType ?? 'json' });
}

export async function postForm<T = unknown>(endpoint: string, form: FormData, opts?: Partial<RequestOptions>) {
  return request<T>({ method: 'POST', endpoint, body: form, ...(opts || {}), responseType: opts?.responseType ?? 'json' });
}
